import axios from 'axios';
import debounce from 'lodash.debounce';
import {
    addAnnotationRequest,
    deleteAnnotationRequest,
    saveAnnotationRequest,
    getStreetviewImageFirstRequest,
    getStreetviewImageLastRequest,
    getStreetviewImagePreviousRequest,
    getStreetviewImageNextRequest,
    getStreetViewRequest,
    updateAttributeValueOfRoadDataRequest,
    getImageByRoadDataRequest,
    getImageByCumulStartRequest,
    getImageByIdRequest,
    downloadSessionBlobImageRequest,
} from '../../../providers/legacy/datasources/sessionDataSource';
import {
    annotationInEditionSelector,
    currentImageIdSelector,
    currentImageSelector,
    isNextImageIndexLoadedSelector,
    isPreviousImageIndexLoadedSelector,
    getLoadedImageIndexesSelector,
    roadDataByIdSelector,
    currentImageUrlSelector,
    temporaryDeformedAnnotationToEditSelector,
    currentImageCumulStartSelector,
    selectedRoadDataIdSelector,
    allRoadDatasIdOfCurrentImageSelector,
    imagesSelector,
    roadDatasToDisplaySelector,
} from '../selectors/streetView';
import { convertAnnotationPointsToRealSize } from '../utils/roadViewerUtils';
import { findRoadDataIdToScrollTo, getAdjustedCumulStart } from '../utils/roadDataUtils';
import { loaderStarted, loaderStopped } from '../../loader/state';
import {
    applyFilterToImageQuery,
    imageRoaddataOnlyQuery,
    referenceToDisplayIds,
    rubricToDisplayIdsQuery,
    taggedElementsOnlyQuery,
} from '../../filters/queries';
import { allReferenceIdsQuery, allRubricsIdsQuery } from '../../catalogs/queries';
import {
    currentImageIndexQuery,
    sessionCalibrationQuery,
    sessionIdQuery,
    sessionQuery,
} from '../../streetViewer/queries';
import {
    currentImageCumulUpdated,
    currentImageIndexDecremented,
    currentImageIndexIncremented,
    currentImageIndexSetToFirst,
    currentImageIndexSetToLast,
    currentImageIndexUpdated,
    currentImagePositionResolved,
    currentLandmarkUpdated,
    resetStarted as resetStartedForStreetViewer,
    sessionUpdated,
} from '../../streetViewer/state';
import { retrieveRoadDataIntervalOfCurrentImage } from '../../actions/streetView';
import { getAnnotationsAndRoadDataForImageRequest } from '../../../providers/datasources/imageDataSource';
import { LoadItineraryForOneSessionCommand } from '../../sessions/commands/LoadItineraryForOneSessionCommand';

// Rubric

export const selectRubric = (rubric) => {
    return {
        type: 'STREET_VIEW/SELECT_RUBRIC',
        rubric,
    };
};

export const unselectRubric = () => {
    return {
        type: 'STREET_VIEW/UNSELECT_RUBRIC',
    };
};

// Road data

export const selectRoadDataInEdition = (roadDataId) => ({
    type: 'STREET_VIEW/SELECT_ROAD_DATA_IN_EDITION',
    roadDataId,
});

export const noMoreRoadDataInEdition = () => ({
    type: 'STREET_VIEW/NO_MORE_ROAD_DATA_IN_EDITION',
});

export const selectSelectedRoadData = (roadDataId) => (dispatch) => {
    dispatch(noMoreAnnotationInEdition());
    dispatch({
        type: 'STREET_VIEW/SELECT_SELECTED_ROAD_DATA',
        roadDataId,
    });
    dispatch(updateRoadDataIdToScrollTo(roadDataId));
};

export const noMoreSelectedRoadData = () => ({
    type: 'STREET_VIEW/NO_MORE_SELECTED_ROAD_DATA',
});

export const selectHoveredRoadData = (roadDataId) => ({
    type: 'STREET_VIEW/SELECT_HOVERED_ROAD_DATA',
    roadDataId,
});

export const noMoreHoveredRoadData = () => ({
    type: 'STREET_VIEW/NO_MORE_HOVERED_ROAD_DATA',
});

export const updateRoadDataIdToScrollTo = (roadDataIdToScrollTo) => ({
    type: 'STREET_VIEW/UPDATE_ROAD_DATA_ID_TO_SCROLL_TO',
    roadDataIdToScrollTo,
});

export const downloadSessionImage = () => async (_dispatch, getState) => {
    const sessionName = sessionQuery(getState())
        ?.name.replace(/[\s!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g, '_')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');
    const imageUrl = currentImageUrlSelector(getState());
    const imageIndex = currentImageIndexQuery(getState());
    const numberForImageName = imageIndex.toString().padStart(4, '0');
    const blob = await downloadSessionBlobImageRequest(imageUrl);
    const a = document.createElement('a');
    a.href = blob;
    a.download = `${sessionName}_image_${numberForImageName}.jpeg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
};

export const roadDataIdToDelete = (id) => async (dispatch, getState) => {
    dispatch(loaderStarted());
    const state = getState();
    const roadDataToDelete = roadDataByIdSelector(id)(state);
    if (roadDataToDelete === undefined) {
        dispatch(loaderStopped());

        return;
    }
    await deleteAnnotationRequest(id);
    await dispatch({
        type: 'STREET_VIEW/DELETE_ROADDATA',
        id,
    });

    // refresh timeline
    const sessionId = sessionIdQuery(state);
    if (sessionId) {
        await dispatch(LoadItineraryForOneSessionCommand({ sessionId }));
    }

    // Update impacted roaddata
    if (roadDataToDelete.isReferentialLandmarks || roadDataToDelete.isSurfaceSections) {
        await dispatch(retrieveRoadDataIntervalOfCurrentImage());
    }

    await dispatch(loaderStopped());
};

export const updateAttributeValueOfRoadData = (roadData, attributeValue, newValue) => async (dispatch, getState) => {
    dispatch(loaderStarted());
    const sessionId = sessionIdQuery(getState());
    const updatedRoadData = await updateAttributeValueOfRoadDataRequest(sessionId, roadData, attributeValue, newValue);
    await dispatch({
        type: 'STREET_VIEW/UPDATED_ROAD_DATA',
        updatedRoadData,
    });

    await dispatch(retrieveAnnotationsForCurrentImage());

    dispatch(loaderStopped());
};

// Annotation

export const selectAnnotationInEdition = (roadDataId) => ({
    type: 'STREET_VIEW/SELECT_ANNOTATION_IN_EDITION',
    roadDataId,
});

export const noMoreAnnotationInEdition = () => ({
    type: 'STREET_VIEW/NO_MORE_ANNOTATION_IN_EDITION',
});

export const updateTemporaryDeformedAnnotationToEdit = (temporaryDeformedAnnotationToEdit) => ({
    type: 'STREET_VIEW/UPDATE_TEMPORARY_DEFORMED_ANNOTATION_TO_EDIT',
    temporaryDeformedAnnotationToEdit,
});
export const resetTemporaryDeformedAnnotationToEdit = () => ({
    type: 'STREET_VIEW/RESET_TEMPORARY_DEFORMED_ANNOTATION_TO_EDIT',
});

const getRoadDataIdToScrollTo = (state, currentImageCumulStart) => {
    let roadDataIdToScrollTo;
    const selectedRoadDataId = selectedRoadDataIdSelector(state);
    const allRoadDatasIdOfCurrentImage = allRoadDatasIdOfCurrentImageSelector(state);
    const isRoadDataOnImage = allRoadDatasIdOfCurrentImage.includes(selectedRoadDataId);
    if (isRoadDataOnImage) {
        roadDataIdToScrollTo = selectedRoadDataId;
    } else {
        const roadDatas = roadDatasToDisplaySelector(state);
        const adjustedCumulStart = getAdjustedCumulStart(currentImageCumulStart);
        roadDataIdToScrollTo = findRoadDataIdToScrollTo(adjustedCumulStart, roadDatas);
    }

    return roadDataIdToScrollTo;
};

export const updateScroll = () => (dispatch, getState) => {
    const state = getState();

    const currentImageCumulStart = currentImageCumulStartSelector(state);
    if (currentImageCumulStart === undefined) {
        return;
    }

    const roadDataIdToScrollTo = getRoadDataIdToScrollTo(state, currentImageCumulStart);
    dispatch(updateRoadDataIdToScrollTo(roadDataIdToScrollTo));
};

// Shared across every call to retrieveAnnotationsForCurrentImage, regardless of caller, so
// that starting a new fetch always cancels whichever one is still pending — there's no
// point letting a request for an image we've since left keep running on the server (and
// holding a connection-pool slot) once we know its answer won't be used.
let currentRoadDataAbortController;

export const retrieveAnnotationsForCurrentImage = () => async (dispatch, getState) => {
    const state = getState();
    const image = currentImageSelector(state);
    const calibration = sessionCalibrationQuery(state);
    const applyFilterToImage = applyFilterToImageQuery(state);
    const taggedElementsOnly = taggedElementsOnlyQuery(state);
    const imageRoaddataOnly = imageRoaddataOnlyQuery(state);
    const rawRubricToDisplayIds = rubricToDisplayIdsQuery(state);
    const rawReferenceToDisplay = referenceToDisplayIds(state);

    // The roaddata list/panel has always ignored the "apply filter to image" toggle and
    // shown your raw filter selections regardless — only the on-image annotation overlay
    // respects that toggle, falling back to "show everything" when it's off. These two
    // views can therefore need genuinely different effective filters; sending one shared
    // set for both (as this used to, briefly) silently made the panel start respecting a
    // toggle it never used to.
    const annotationsFilters = {
        rubricToDisplayIds: applyFilterToImage ? rawRubricToDisplayIds : allRubricsIdsQuery(state),
        referenceToDisplayIds: applyFilterToImage ? rawReferenceToDisplay : allReferenceIdsQuery(state),
        taggedElementsOnly: applyFilterToImage ? taggedElementsOnly : false,
        imageRoaddataOnly: applyFilterToImage ? imageRoaddataOnly : false,
    };
    const intervalFilters = {
        rubricToDisplayIds: rawRubricToDisplayIds,
        referenceToDisplayIds: rawReferenceToDisplay,
        taggedElementsOnly,
        imageRoaddataOnly,
    };

    if (!image || !calibration) {
        return;
    }

    if (currentRoadDataAbortController) {
        currentRoadDataAbortController.abort();
    }
    const abortController = new AbortController();
    currentRoadDataAbortController = abortController;
    const { signal } = abortController;

    try {
        const { annotations, roaddatas, totalSize, rubricIdToCount } = await getAnnotationsAndRoadDataForImageRequest(
            image.id,
            annotationsFilters,
            intervalFilters,
            signal,
        );

        // Belt-and-suspenders: the abort above should already keep a stale response from
        // getting here, but if this request happened to resolve right as it was being
        // superseded, don't let it clobber whatever image is now actually on screen.
        if (currentImageIdSelector(getState()) !== image.id) {
            return;
        }

        dispatch({
            type: 'STREET_VIEW/UPDATE_ANNOTATIONS',
            annotations,
        });
        dispatch({
            type: 'STREET_VIEW/UPDATE_ROAD_DATAS',
            roadDatas: roaddatas,
            totalSize,
            rubricIdToCount,
        });
    } catch (e) {
        // A cancelled request just means a newer navigation superseded this one — not a
        // real failure, nothing to report.
        if (!axios.isCancel(e)) {
            throw e;
        }
    }
};

// Navigating quickly through images (holding the arrow key, spamming the next/previous
// button) used to fire one roaddata-interval + annotations request per image visited.
// Those requests aren't cancelled, so on a fast burst they pile up and race each other on
// the network; whichever happens to resolve last wins, which is often not the image the
// user actually stopped on.
//
// Debounced on both edges: the *first* step in a sequence fetches immediately (leading
// edge), so a single deliberate navigation feels exactly as fast as before, while further
// navigation within ROAD_DATA_SETTLE_DELAY_MS is suppressed and one more fetch fires once
// the user actually stops (trailing edge) — so a fast burst results in at most two requests
// (one for wherever it started, one for wherever it settled) instead of one per image
// passed through. The AbortController inside retrieveAnnotationsForCurrentImage still
// cancels the leading fetch if the trailing one supersedes it before it resolves.
const ROAD_DATA_SETTLE_DELAY_MS = 350;
const debouncedFetchRoadDataForCurrentImage = debounce(
    async (dispatch) => {
        await dispatch(retrieveAnnotationsForCurrentImage());
        // updateScroll depends on the roadDatas this fetch just loaded, so it has to run
        // right after it — from here, not chained by the caller, since this may resolve on
        // either the leading or the trailing edge and the caller can't know which.
        dispatch(updateScroll());
    },
    ROAD_DATA_SETTLE_DELAY_MS,
    { leading: true, trailing: true },
);

export const debouncedRetrieveAnnotationsForCurrentImage = () => (dispatch) => {
    debouncedFetchRoadDataForCurrentImage(dispatch);
};

export const addAnnotation = (annotationPoints, rubricId, reference) => async (dispatch, getState) => {
    dispatch(loaderStarted());
    const state = getState();
    const imageId = currentImageIdSelector(state);

    const annotation = await addAnnotationRequest({ rubricId, imageId, annotationPoints, reference });
    await dispatch({
        type: 'STREET_VIEW/ADD_ANNOTATION',
        annotation,
    });

    // Update impacted roaddata
    if (
        annotation.roadDataChanged &&
        (annotation.roadData.isReferentialLandmarks || annotation.roadData.isSurfaceSections)
    ) {
        await dispatch(retrieveRoadDataIntervalOfCurrentImage());
    }

    const rubricToDisplayIds = rubricToDisplayIdsQuery(state);
    if (!rubricToDisplayIds.includes(rubricId)) {
        dispatch({
            type: 'FILTERS/UPDATE_RUBRIC_TO_DISPLAY_IDS',
            rubricToDisplayIds: [...rubricToDisplayIds, rubricId],
        });
    }

    // refresh timeline
    const sessionId = sessionIdQuery(state);
    if (sessionId) {
        await dispatch(LoadItineraryForOneSessionCommand({ sessionId }));
    }

    await dispatch(selectSelectedRoadData(annotation.roadData.id));
    dispatch(loaderStopped());
};

export const saveEditedAnnotationPoints = (annotationPoints) => async (dispatch, getState) => {
    dispatch(loaderStarted());
    const state = getState();
    const editedAnnotation = annotationInEditionSelector(state);
    await dispatch({
        type: 'STREET_VIEW/UPDATE_ANNOTATION_PRESET_ATTRIBUTE_SHAPE',
        annotation: editedAnnotation,
        projectedShape: annotationPoints,
    });

    await dispatch(noMoreAnnotationInEdition());
    const imageId = currentImageIdSelector(state);
    const updatedAnnotation = await saveAnnotationRequest(imageId, editedAnnotation, annotationPoints);
    await dispatch({
        type: 'STREET_VIEW/UPDATE_ANNOTATION',
        annotation: updatedAnnotation,
    });

    // refresh timeline
    const sessionId = sessionIdQuery(state);
    if (sessionId) {
        await dispatch(LoadItineraryForOneSessionCommand({ sessionId }));
    }

    // Update impacted roaddata
    if (
        updatedAnnotation.roadDataChanged &&
        (updatedAnnotation.roadData.isReferentialLandmarks || updatedAnnotation.roadData.isSurfaceSections)
    ) {
        await dispatch(retrieveRoadDataIntervalOfCurrentImage());
    }

    dispatch(loaderStopped());
};

export const saveEditedAnnotation = () => async (dispatch, getState) => {
    const state = getState();
    const temporaryDeformedAnnotationToEdit = temporaryDeformedAnnotationToEditSelector(state);
    if (temporaryDeformedAnnotationToEdit) {
        await dispatch(
            saveEditedAnnotationPoints(
                convertAnnotationPointsToRealSize(temporaryDeformedAnnotationToEdit.coordinates),
            ),
        );
    }
    await dispatch(noMoreAnnotationInEdition());
};

// The shapes/points drawn on the image come from state.streetView.annotations, which is
// only refreshed once a (possibly debounced) fetch for the new image completes. Without
// clearing it immediately on navigation, the previous image's shapes keep rendering — now
// positioned over a different image, describing a different location — until that fetch
// catches up. Called on every navigation step, not debounced, so stale shapes never
// persist even mid-burst.
export const clearAnnotationsDisplay = () => (dispatch) => {
    dispatch({
        type: 'STREET_VIEW/UPDATE_ANNOTATIONS',
        annotations: [],
    });
};

export const refreshImageLandmark = () => async (dispatch, getState) => {
    const currentImage = currentImageSelector(getState());
    if (currentImage) {
        const { landmark } = currentImage;
        dispatch(currentLandmarkUpdated(landmark));
    }
};

// Player

export const playerGoStart = () => async (dispatch, getState) => {
    await dispatch(saveEditedAnnotation());

    const sessionId = sessionIdQuery(getState());
    // actions below only return images data, since the current session data is stored
    const imagesFromSession = await getStreetviewImageFirstRequest(sessionId);
    await dispatch({
        type: 'STREET_VIEW/UPDATE_IMAGE_CACHE',
        images: imagesFromSession,
    });

    dispatch(currentImageIndexSetToFirst(imagesSelector(getState())));
    dispatch(clearAnnotationsDisplay());
    await dispatch(retrieveAnnotationsForCurrentImage());
    await dispatch(refreshImageLandmark());
    await dispatch(updateScroll());
};

export const playerGoEnd = () => async (dispatch, getState) => {
    await dispatch(saveEditedAnnotation());

    const sessionId = sessionIdQuery(getState());
    const imagesFromSession = await getStreetviewImageLastRequest(sessionId);
    await dispatch({
        type: 'STREET_VIEW/UPDATE_IMAGE_CACHE',
        images: imagesFromSession,
    });

    const images = imagesSelector(getState());
    dispatch(currentImageIndexSetToLast(images));
    dispatch(clearAnnotationsDisplay());
    await dispatch(retrieveAnnotationsForCurrentImage());
    await dispatch(refreshImageLandmark());
    await dispatch(updateScroll());
};

export const playerGoPrevious = () => async (dispatch, getState) => {
    await dispatch(saveEditedAnnotation());

    const sessionId = sessionIdQuery(getState());
    const currentImageIndex = currentImageIndexQuery(getState());
    // Preloading, if expected image not already loaded
    const needPreloading = !isPreviousImageIndexLoadedSelector(getState());
    if (needPreloading) {
        const imagesFromSession = await getStreetviewImagePreviousRequest(sessionId, currentImageIndex);
        await dispatch({
            type: 'STREET_VIEW/UPDATE_IMAGE_CACHE',
            images: imagesFromSession,
        });
    }

    // Change current index
    dispatch(currentImageIndexDecremented());
    dispatch(clearAnnotationsDisplay());
    await dispatch(refreshImageLandmark());

    // Postloading, to speed up next change — kept off the roaddata/annotations debounce
    // below so a fast burst of navigation still keeps the preload window filled and image
    // switching itself stays snappy.
    const needPostloading = !isPreviousImageIndexLoadedSelector(getState());
    if (needPostloading) {
        const imagesFromSession = await getStreetviewImagePreviousRequest(sessionId, currentImageIndex);
        await dispatch({
            type: 'STREET_VIEW/UPDATE_IMAGE_CACHE',
            images: imagesFromSession,
        });
    }
    // updateScroll is dispatched from inside debouncedRetrieveAnnotationsForCurrentImage,
    // right after the fetch it's actually gated on — it may resolve on the leading or the
    // trailing edge, so it can't be reliably chained here any more.
    dispatch(debouncedRetrieveAnnotationsForCurrentImage());
};

export const playerGoNext = () => async (dispatch, getState) => {
    await dispatch(saveEditedAnnotation());
    const sessionId = sessionIdQuery(getState());
    const currentImageIndex = currentImageIndexQuery(getState());
    // Preloading, if expected image not already loaded
    const needPreloading = !isNextImageIndexLoadedSelector(getState());
    if (needPreloading) {
        const imagesFromSession = await getStreetviewImageNextRequest(sessionId, currentImageIndex);
        await dispatch({
            type: 'STREET_VIEW/UPDATE_IMAGE_CACHE',
            images: imagesFromSession,
        });
    }

    const images = imagesSelector(getState());
    dispatch(currentImageIndexIncremented(images));
    dispatch(clearAnnotationsDisplay());
    await dispatch(refreshImageLandmark());

    // Postloading, to speed up next change — kept off the roaddata/annotations debounce
    // below so a fast burst of navigation still keeps the preload window filled and image
    // switching itself stays snappy.
    const needPostloading = !isNextImageIndexLoadedSelector(getState());
    if (needPostloading) {
        const imagesFromSession = await getStreetviewImageNextRequest(sessionId, currentImageIndex);
        await dispatch({
            type: 'STREET_VIEW/UPDATE_IMAGE_CACHE',
            images: imagesFromSession,
        });
    }

    // updateScroll is dispatched from inside debouncedRetrieveAnnotationsForCurrentImage,
    // right after the fetch it's actually gated on — it may resolve on the leading or the
    // trailing edge, so it can't be reliably chained here any more.
    dispatch(debouncedRetrieveAnnotationsForCurrentImage());
};

// Ensures the adjacent images (index-1/index+1) are cached after jumping straight to an image,
// so consumers like MiniMapPiP can compute travel direction from real coordinates instead of
// falling back to an ambiguous nearest-point search on the session's line geometry.
const preloadAdjacentImages = (sessionId, imageIndex) => async (dispatch, getState) => {
    const fetches = [];
    if (!isNextImageIndexLoadedSelector(getState())) {
        fetches.push(getStreetviewImageNextRequest(sessionId, imageIndex));
    }
    if (!isPreviousImageIndexLoadedSelector(getState())) {
        fetches.push(getStreetviewImagePreviousRequest(sessionId, imageIndex));
    }
    if (fetches.length === 0) {
        return;
    }

    const results = await Promise.all(fetches);
    await dispatch({
        type: 'STREET_VIEW/UPDATE_IMAGE_CACHE',
        images: results.flat(),
    });
};

export const goToImageByRoadData = (roadDataId) => async (dispatch, getState) => {
    await dispatch(saveEditedAnnotation());
    await dispatch(noMoreHoveredRoadData());
    await dispatch(selectSelectedRoadData(roadDataId));
    dispatch(selectSelectedRoadData(roadDataId));
    dispatch(updateRoadDataIdToScrollTo(roadDataId));
    const sessionId = sessionIdQuery(getState());
    const image = await getImageByRoadDataRequest(sessionId, roadDataId);
    const loadedImageIndexes = getLoadedImageIndexesSelector(getState());
    const isAlreadyLoaded = loadedImageIndexes.includes(image.index);
    if (!isAlreadyLoaded) {
        await dispatch({
            type: 'STREET_VIEW/UPDATE_IMAGE_CACHE',
            images: [image],
        });
    }
    dispatch(currentImageIndexUpdated(image.index));
    dispatch(clearAnnotationsDisplay());
    await dispatch(preloadAdjacentImages(sessionId, image.index));
    await dispatch(retrieveAnnotationsForCurrentImage());
    await dispatch(refreshImageLandmark());
};

export const unselectRoadDataAndSave = (roadDataIdToHover) => async (dispatch) => {
    await dispatch(saveEditedAnnotation());
    await dispatch(noMoreSelectedRoadData());
    await dispatch(selectHoveredRoadData(roadDataIdToHover));
};

export const unselectRubricWithoutSaving = () => async (dispatch) => {
    await dispatch(noMoreAnnotationInEdition());
    await dispatch(unselectRubric());
};
export const cancelAnnotationCreationAndEdition = () => async (dispatch) => {
    await dispatch(noMoreSelectedRoadData());
    await dispatch(unselectRubricWithoutSaving());
};
export const selectAnnotationInEditionAndJump = (roadDataId) => async (dispatch) => {
    await dispatch(goToImageByRoadData(roadDataId));
    await dispatch(selectAnnotationInEdition(roadDataId));
};

export const goToImageByCumul = (cumulStart) => async (dispatch, getState) => {
    await dispatch(saveEditedAnnotation());
    const sessionId = sessionIdQuery(getState());
    let image;
    if (sessionId) {
        image = await getImageByCumulStartRequest(sessionId, cumulStart);
    }
    if (!image) {
        return;
    }
    const loadedImageIndexes = getLoadedImageIndexesSelector(getState());
    const isAlreadyLoaded = loadedImageIndexes.includes(image.index);
    if (!isAlreadyLoaded) {
        await dispatch({
            type: 'STREET_VIEW/UPDATE_IMAGE_CACHE',
            images: [image],
        });
    }
    await dispatch(currentImageIndexUpdated(image.index));
    dispatch(currentImagePositionResolved());
    dispatch(clearAnnotationsDisplay());
    await dispatch(preloadAdjacentImages(sessionId, image.index));
    if (sessionId) {
        dispatch(
            currentImageCumulUpdated({
                sessionId,
                cumul: cumulStart,
            }),
        );
    }
    await dispatch(refreshImageLandmark());
    await dispatch(retrieveAnnotationsForCurrentImage());
    await dispatch(updateScroll());
};

export const goToImageById = (imageId) => async (dispatch, getState) => {
    await dispatch(saveEditedAnnotation());
    const sessionId = sessionIdQuery(getState());
    let image;
    if (sessionId) {
        image = await getImageByIdRequest(sessionId, imageId);
    }
    if (!image) {
        return;
    }
    const loadedImageIndexes = getLoadedImageIndexesSelector(getState());
    const isAlreadyLoaded = loadedImageIndexes.includes(image.index);
    if (!isAlreadyLoaded) {
        await dispatch({
            type: 'STREET_VIEW/UPDATE_IMAGE_CACHE',
            images: [image],
        });
    }
    await dispatch(currentImageIndexUpdated(image.index));
    dispatch(currentImagePositionResolved());
    dispatch(clearAnnotationsDisplay());
    await dispatch(preloadAdjacentImages(sessionId, image.index));
    if (sessionId) {
        dispatch(
            currentImageCumulUpdated({
                sessionId,
                cumul: image.cumulStart,
            }),
        );
    }
    await dispatch(refreshImageLandmark());
    await dispatch(retrieveAnnotationsForCurrentImage());
    await dispatch(updateScroll());
};

// Init street view

export const resetStreetView = () => (dispatch) => {
    dispatch({
        type: 'STREET_VIEW/RESET',
    });
    dispatch({
        type: 'CATALOGS/SELECTION_RESET',
    });
};

export const initStreetView = (sessionId) => async (dispatch) => {
    dispatch(resetStartedForStreetViewer());
    const streetViewFromSession = await getStreetViewRequest(sessionId);
    const { images, ...session } = streetViewFromSession;
    dispatch({
        type: 'STREET_VIEW/INIT',
        images: images ?? [],
    });
    dispatch(sessionUpdated(session));
};
